import React, { useState } from 'react';
import { Search, X, UserCircle } from 'lucide-react';
import { AdminApi } from '../../services/api';
import { IssueType, TicketPriority, type Ticket } from '../../types';
import { Button, Modal } from '../../shared/ui';
import { ISSUE_TYPE_LABELS, PRIORITY_LABELS } from '../../shared/labels';
import { EQUIPEMENT_MAX } from './equipement';
import { DESCRIPTION_MAX, SAISIE_VIDE, TITRE_MAX, verifierNouveauDossier, versPayloadCreation, type SaisieNouveauDossier } from './creationDossier';
import { assurerCompteSav, messageErreurCompteSav, noteValidation, useRechercheClients, type ClientChoisi } from './rechercheClients';
import { ListeResultatsClients } from './ListeResultatsClients';

// Mobile et tablette : cibles tactiles d'au moins 40 px, boutons de validation
// empilés pleine largeur sur téléphone. À partir de sm / lg, rendu d'origine.
const TACTILE = 'min-h-[40px] lg:min-h-0';
// Sur téléphone, les boutons restent collés en bas de la fenêtre qui défile.
const ACTIONS_FENETRE = 'flex flex-col gap-2 sm:flex-row sm:justify-end max-sm:sticky max-sm:-bottom-5 max-sm:z-10 max-sm:bg-white max-sm:-mx-5 max-sm:px-5 max-sm:pb-5 max-sm:pt-3 max-sm:-mb-5';
const BOUTON_FENETRE = `w-full sm:w-auto ${TACTILE}`;

// 16 px sur téléphone : pas de zoom automatique d'iOS au focus.
const champ = 'mt-1 block w-full rounded-lg border border-trait bg-white py-2 px-3 text-base sm:text-sm text-encre placeholder:text-graphite/70 focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none';

/**
 * Ouverture manuelle d'un dossier (appel téléphonique, visite). Le client est
 * un compte SAV, un client de klygroupe.com (décision du 16/10/2026) ou un
 * contact libre. La recherche interroge en parallèle les comptes SAV et la
 * boutique (rechercheClients.ts). Choisir un client de la boutique ne crée
 * rien : son compte SAV est créé, ou vérifié, par le serveur au clic sur
 * « Créer le dossier », juste avant la création (assurerCompteSav), et le
 * dossier reçoit le compte rendu par le serveur. Annuler ne laisse rien.
 * `clientInitial` : client déjà sélectionné (écran Clients). À la création,
 * la fiche du nouveau dossier s'ouvre.
 */
export const NouveauDossierModal: React.FC<{
  onClose: () => void;
  onCree: (dossier: Ticket) => void;
  clientInitial?: ClientChoisi | null;
}> = ({ onClose, onCree, clientInitial = null }) => {
  // customerId n'est fixé qu'à l'envoi : celui d'un client de la boutique
  // n'existe qu'après la validation.
  const [saisie, setSaisie] = useState<SaisieNouveauDossier>(SAISIE_VIDE);
  const [client, setClient] = useState<ClientChoisi | null>(clientInitial);
  const [recherche, setRecherche] = useState('');
  const etatRecherche = useRechercheClients(recherche, !client);
  const [erreurs, setErreurs] = useState<Partial<Record<keyof SaisieNouveauDossier, string>>>({});
  const [envoi, setEnvoi] = useState(false);
  const [erreurServeur, setErreurServeur] = useState<string | null>(null);

  const maj = <K extends keyof SaisieNouveauDossier>(cle: K, valeur: SaisieNouveauDossier[K]) =>
    setSaisie(s => ({ ...s, [cle]: valeur }));

  const choisirClient = (c: ClientChoisi) => {
    setClient(c);
    setErreurServeur(null);
    setRecherche('');
  };

  const retirerClient = () => {
    setClient(null);
    setErreurServeur(null);
  };

  const envoyer = async (e: React.FormEvent) => {
    e.preventDefault();
    // Un client sélectionné suffit au contrôle, même si son compte SAV
    // (client de la boutique) ne sera établi qu'ici, à l'envoi.
    const verif = verifierNouveauDossier({ ...saisie, customerId: client ? (client.id ?? `boutique:${client.boutiqueId}`) : '' });
    setErreurs(verif);
    if (Object.keys(verif).length > 0) return;
    setEnvoi(true);
    setErreurServeur(null);
    try {
      let customerId = '';
      if (client) {
        let compte;
        try {
          compte = await assurerCompteSav(client);
        } catch (err) {
          setErreurServeur(messageErreurCompteSav(err));
          return;
        }
        // Compte établi : un nouvel essai ne le redemande pas au serveur.
        setClient(compte);
        customerId = compte.id;
      }
      const cree = await AdminApi.createTicket(versPayloadCreation({ ...saisie, customerId }));
      // La réponse de création ne porte pas toujours les relations : on relit le dossier.
      const complet = await AdminApi.getTicketById(cree.id).catch(() => cree);
      onCree(complet);
    } catch (err) {
      setErreurServeur(err instanceof Error ? err.message : 'La création a échoué');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Nouveau dossier" size="lg">
      <form onSubmit={envoyer} className="space-y-5" noValidate>
        {/* Client */}
        <fieldset>
          <legend className="text-xs font-semibold text-graphite">Client</legend>
          {client ? (
            <div className="mt-2 flex items-center gap-3 rounded-lg border border-trait bg-brume px-3 py-2">
              <UserCircle className="w-5 h-5 text-graphite shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-encre truncate">{client.displayName}</p>
                <p className="text-xs text-graphite truncate">{client.email}{client.phone ? ` · ${client.phone}` : ''}{client.customerCode ? ` · Code SAGE ${client.customerCode}` : ''}</p>
                {noteValidation(client) && <p className="text-xs text-sapin-700">{noteValidation(client)}</p>}
              </div>
              <button type="button" onClick={retirerClient} disabled={envoi} aria-label="Retirer le client" className="p-3 -m-2 lg:p-1 lg:m-0 text-graphite hover:text-encre"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-graphite pointer-events-none" />
                <input
                  value={recherche}
                  onChange={e => setRecherche(e.target.value)}
                  placeholder="Client : société, nom, e-mail, téléphone ou n° de compte (facultatif)"
                  className={`${champ} mt-0 pl-9`}
                  aria-label="Rechercher un client existant"
                />
              </div>
              <ListeResultatsClients
                recherche={recherche}
                etat={etatRecherche}
                onChoisi={choisirClient}
                messageVide="Aucun client trouvé dans le SAV ni sur klygroupe.com : renseignez le contact ci-dessous."
              />
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-graphite">Nom du contact</span>
                  <input value={saisie.contactName} onChange={e => maj('contactName', e.target.value)} className={champ} autoComplete="off" /></label>
                <label className="block"><span className="text-xs text-graphite">Société</span>
                  <input value={saisie.companyName} onChange={e => maj('companyName', e.target.value)} className={champ} autoComplete="off" /></label>
                <label className="block"><span className="text-xs text-graphite">E-mail</span>
                  <input type="email" value={saisie.contactEmail} onChange={e => maj('contactEmail', e.target.value)} className={champ} autoComplete="off" /></label>
                <label className="block"><span className="text-xs text-graphite">Téléphone</span>
                  <input type="tel" value={saisie.contactPhone} onChange={e => maj('contactPhone', e.target.value)} className={champ} autoComplete="off" /></label>
              </div>
              {erreurs.contactName && <p role="alert" className="mt-1 text-xs text-red-700">{erreurs.contactName}</p>}
              {erreurs.contactEmail && <p role="alert" className="mt-1 text-xs text-red-700">{erreurs.contactEmail}</p>}
            </>
          )}
        </fieldset>

        {/* Demande */}
        <fieldset className="space-y-3">
          <legend className="text-xs font-semibold text-graphite">Demande</legend>
          <label className="block"><span className="text-xs text-graphite">Titre</span>
            <input value={saisie.title} onChange={e => maj('title', e.target.value)} maxLength={TITRE_MAX} className={champ} placeholder="Ex. : PAC en défaut E04 après coupure de courant" required /></label>
          {erreurs.title && <p role="alert" className="text-xs text-red-700">{erreurs.title}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-graphite">Type</span>
              <select value={saisie.issueType} onChange={e => maj('issueType', e.target.value as IssueType)} className={champ}>
                {Object.entries(ISSUE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="block"><span className="text-xs text-graphite">Priorité</span>
              <select value={saisie.priority} onChange={e => maj('priority', e.target.value as TicketPriority)} className={champ}>
                {Object.entries(PRIORITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
          </div>
          <label className="block"><span className="text-xs text-graphite">Description</span>
            <textarea value={saisie.description} onChange={e => maj('description', e.target.value)} maxLength={DESCRIPTION_MAX} rows={4} className={`${champ} resize-none`} placeholder="Ce que le client a décrit au téléphone" /></label>
          {erreurs.description && <p role="alert" className="text-xs text-red-700">{erreurs.description}</p>}
        </fieldset>

        {/* Équipement */}
        <fieldset>
          <legend className="text-xs font-semibold text-graphite">Équipement (facultatif)</legend>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-graphite">Marque</span>
              <input value={saisie.equipmentBrand} onChange={e => maj('equipmentBrand', e.target.value)} maxLength={EQUIPEMENT_MAX} className={champ} /></label>
            <label className="block"><span className="text-xs text-graphite">Modèle</span>
              <input value={saisie.equipmentModel} onChange={e => maj('equipmentModel', e.target.value)} maxLength={EQUIPEMENT_MAX} className={champ} /></label>
            <label className="block"><span className="text-xs text-graphite">N° de série</span>
              <input value={saisie.serialNumber} onChange={e => maj('serialNumber', e.target.value)} maxLength={EQUIPEMENT_MAX} className={`${champ} tnum`} /></label>
            <label className="block"><span className="text-xs text-graphite">Code erreur</span>
              <input value={saisie.errorCode} onChange={e => maj('errorCode', e.target.value)} maxLength={EQUIPEMENT_MAX} className={`${champ} tnum`} /></label>
          </div>
        </fieldset>

        {erreurServeur && <p role="alert" className="text-sm text-red-700">Création impossible : {erreurServeur}</p>}

        <div data-testid="actions-fenetre" className={`${ACTIONS_FENETRE} pt-2 border-t border-trait`}>
          <Button variant="secondary" onClick={onClose} disabled={envoi} className={BOUTON_FENETRE}>Annuler</Button>
          <Button type="submit" variant="primary" disabled={envoi} className={BOUTON_FENETRE}>{envoi ? 'Création…' : 'Créer le dossier'}</Button>
        </div>
      </form>
    </Modal>
  );
};

export default NouveauDossierModal;
